import React, { useState, useEffect, ChangeEvent } from 'react'; // Removed useRef if not used after flatpickr removal
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query'; // Added useQueryClient
import { toast } from 'react-toastify';
import { formatDateWithTime } from '../../../helper-functions/DateFunction';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';
import {
  change_active_status_vendor,
  delete_vendor,
  get_all_filters_status_vendors,
} from '../api/vendorsApi';
import { KTIcon } from '../../../../_metronic/helpers';

// --- Type Definitions ---
type Vendor = {
  id: number;
  supplier_type: string | undefined;
  supplier_name: string | undefined;
  email: string | undefined;
  mobile_no: string | undefined;
  telephone: string | undefined;
  address_line_1: string | undefined;
  address_line_2: string | undefined;
  town: string | undefined;
  city: string | undefined;
  post_code: string | undefined;
  status: number; // 0 or 1
  updated_by_name: string | undefined;
  created_at?: string | undefined; // Make optional if not always present
  updated_at: string;
};

type ApiPaginationInfo = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
};

type ApiVendorsDataPayload = {
    vendors: Vendor[]; // Assuming API returns 'vendors' array
    pagination: ApiPaginationInfo;
    links: { /* ... link structure ... */ };
};

type ApiVendorsFullResponse = {
    data: { // From Axios
        success: boolean;
        message: string;
        data: ApiVendorsDataPayload; // From your API structure
    };
};
// --- End Type Definitions ---


var vendor_add:boolean, vendor_edit:boolean, vendor_view:boolean, vendor_logs:boolean, vendor_delete:boolean;

function VendorsList() {
  const [vendorList, setVendorList] = useState<Vendor[]>([]); // For current page

  // Filter States
  const [selectedStatus, setSelectedStatus] = useState<string>(''); // '0', '1', or ''
  const [searchInput, setSearchInput] = useState<string>('');

  // State for API filter payload (only status for now, add search if backend supports)
  const [filterPayloadForApi, setFilterPayloadForApi] = useState<any>({
    status: '',
    // searchTerm: '', // If backend supports search
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10); // Renamed
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentRangeFrom, setCurrentRangeFrom] = useState<number>(1);
  const [currentRangeTo, setCurrentRangeTo] = useState<number>(0);
  
  // const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null); // For server-side sorting

  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  vendor_add = permissionCheck('supplier-create');
  vendor_edit = permissionCheck('supplier-update');
  vendor_view = permissionCheck('supplier-view');
  vendor_logs = permissionCheck('supplier-activity-log');
  vendor_delete = permissionCheck('supplier-delete');


  // --- Main Query for Filtered and Paginated Vendors ---
  const {
    // data: filteredVendorsQueryData, // Full API response
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery<ApiVendorsFullResponse, Error>(
    // Query key now includes all parameters that trigger a refetch
    ['filtered_vendors', filterPayloadForApi, currentPage, itemsPerPage],
    () => get_all_filters_status_vendors( // Using this as the primary data source
      filterPayloadForApi.status,
      currentPage,
      itemsPerPage
      // filterPayloadForApi.searchTerm, // If server-side search
    ),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        const apiPayload = response.data.data;
        // Assuming API returns 'vendors' array
        if (apiPayload && apiPayload.vendors && Array.isArray(apiPayload.vendors)) {
          setVendorList(apiPayload.vendors);
          if (apiPayload.pagination) {
            setTotalItems(apiPayload.pagination.total);
            setTotalPages(apiPayload.pagination.last_page);
            setCurrentPage(apiPayload.pagination.current_page);
            setCurrentRangeFrom(apiPayload.pagination.from);
            setCurrentRangeTo(apiPayload.pagination.to);
          }
        } else {
          setVendorList([]);
          setTotalItems(0);
          setTotalPages(0);
          setCurrentRangeFrom(1);
          setCurrentRangeTo(0);
        }
      },
      onError: (error: any) => {
        toast.error(error.message || "Failed to fetch vendors.", { autoClose: 3000 });
        setVendorList([]);
        setTotalItems(0);
        setTotalPages(0);
      },
    }
  );
  
  // Client-side search (optional, can be moved to server-side)
  const displayedVendorList = searchInput
    ? vendorList.filter((vendor) =>
        vendor.supplier_name?.toLowerCase().includes(searchInput.toLowerCase()) ||
        vendor.email?.toLowerCase().includes(searchInput.toLowerCase()) ||
        vendor.mobile_no?.toLowerCase().includes(searchInput.toLowerCase())
      )
    : vendorList;

  // Mutations
  const handleDeleteVendor = useMutation(
    (id: number) => delete_vendor(id),
    {
      onSuccess: (response) => {
        toast.success(response.message || "Vendor deleted successfully.", { autoClose: 3000 });
        queryClient.invalidateQueries(['filtered_vendors', filterPayloadForApi, currentPage, itemsPerPage]);
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || error.message || "Failed to delete vendor.";
        toast.error(<div dangerouslySetInnerHTML={{ __html: errorMessage }} />, { autoClose: 6000 });
      },
    }
  );

  const handleStatusUpdate = useMutation(
    (id: number) => change_active_status_vendor(id),
    {
      onSuccess: (response) => {
        toast.success(response.message || "Status updated successfully.", { autoClose: 3000 });
        queryClient.invalidateQueries(['filtered_vendors', filterPayloadForApi, currentPage, itemsPerPage]);
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || error.message || "Failed to update status.";
        toast.error(<div dangerouslySetInnerHTML={{ __html: errorMessage }} />, { autoClose: 6000 });
      },
    }
  );

  // Filter change handler
  useEffect(() => {
    // This effect will run when selectedStatus changes,
    // updating filterPayloadForApi and triggering a refetch by react-query.
    setCurrentPage(1); // Reset to page 1 when status filter changes
    setFilterPayloadForApi({
      status: selectedStatus,
      // searchTerm: searchInput, // Include if server-side search is implemented
    });
  }, [selectedStatus]); // Only re-trigger for selectedStatus, searchInput can be handled separately


  // Pagination handlers
  const itemsPerPageOptions = [10, 20, 30, 50, 100];
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Navigation handlers
  const handleVendorLogs = (vendor: Vendor) => navigate("/vendors/vendor-logs", { state: { id: vendor.id } });
  const handleVendorEdit = (vendor: Vendor) => navigate("/vendors/edit-vendor", { state: { vendor: vendor } });
  const handleVendorView = (vendor: Vendor) => navigate("/vendors/view-vendor", { state: { vendor: vendor } });

  // Removed client-side sorting. Server-side sorting needed.

  if (isLoading && !isFetching && currentPage === 1) { // Initial load
    return <div className='d-flex justify-content-center align-items-center' style={{height: '200px'}}><div className='spinner-border text-primary' role='status'><span className='visually-hidden'>Loading...</span></div></div>;
  }

  return (
    <>
      <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10 ' style={{ position: 'relative' }}>
        {/* Card Header */}
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Vendors List</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              {totalItems > 0 ? `${totalItems} vendors found` : 'No vendors'}
            </span>
          </h3>
          <div className='card-toolbar'>
            {vendor_add && (
              <button className='btn btn-sm btn-light-dark' onClick={() => navigate('/vendors/add-vendor')}>
                <KTIcon iconName='plus' className='fs-3' /> Add Vendor
              </button>
            )}
          </div>
        </div>

        {/* Search And Filter */}
        <div className='card-header border-0 pt-5'>
          <div className='w-100 d-flex flex-column flex-md-row justify-content-between align-items-md-center'>
            <div className='mb-3 mb-md-0'>
              <div className='input-group'>
                <span className="input-group-text"><KTIcon iconName='magnifier' className='fs-3' /></span>
                <input
                  type='text'
                  className='form-control form-control-solid w-250px'
                  placeholder='Search vendor, email, mobile...'
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>
            <div className='d-flex align-items-center'>
              <span className='text-muted me-2'>Filter by:</span>
              <select
                className='form-select form-select-solid form-select-sm w-auto'
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                disabled={isFetching}
              >
                <option value=''>All Status</option>
                <option value='1'>Active</option>
                <option value='0'>Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className='card-body py-3 '>
          {isFetching && !isLoading && <div className='text-center text-muted p-3'><i>Updating vendor list...</i></div>}
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 '>
              <thead>
                <tr className='fw-bold text-muted bg-light'>
                  <th className='ps-4 min-w-150px rounded-start'>Vendor Name</th>
                  <th className='min-w-140px'>Mobile No</th>
                  <th className='min-w-150px'>Email</th>
                  <th className='min-w-100px'>Status</th>
                  <th className='min-w-150px'>Updated By</th>
                  <th className='min-w-150px'>Updated At</th>
                  {(vendor_delete || vendor_edit || vendor_logs || vendor_view) &&
                    <th className='min-w-150px text-center rounded-end'>Actions</th>
                  }
                </tr>
              </thead>
              <tbody>
                {isLoading && currentPage === 1 ? (
                  <tr><td colSpan={7} className="text-center py-5"><div className='spinner-border text-primary' /></td></tr>
                ) : isError ? (
                  <tr><td colSpan={7} className="text-center py-5 text-danger">Error: {error?.message}</td></tr>
                ) : displayedVendorList.length > 0 ? (
                  displayedVendorList.map((vendor) => (
                    <tr key={vendor.id}>
                      <td className='ps-4'>
                        <span className='text-dark fw-bold text-hover-primary d-block fs-6 cursor-pointer' onClick={() => handleVendorView(vendor)}>
                          {vendor.supplier_name}
                        </span>
                        <span className='text-muted fw-semibold d-block fs-7'>{vendor.supplier_type}</span>
                      </td>
                      <td><span className='text-dark fw-semibold d-block fs-6'>{vendor.mobile_no}</span></td>
                      <td><span className='text-dark fw-semibold d-block fs-6'>{vendor.email}</span></td>
                      <td>
                        <span className={`badge ${vendor.status === 1 ? 'badge-light-success' : 'badge-light-danger'} fs-7 fw-bold`}>
                          {vendor.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td><span className='text-dark fw-semibold d-block fs-6'>{vendor.updated_by_name || 'N/A'}</span></td>
                      <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithTime(vendor.updated_at)}</span></td>
                      {(vendor_delete || vendor_edit || vendor_logs || vendor_view) &&
                      <td className='text-center'>
                        <div className='d-flex justify-content-center flex-shrink-0'>
                          {vendor_view && (<button onClick={() => handleVendorView(vendor)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="View"><KTIcon iconName='eye' className='fs-3' /></button>)}
                          {vendor_edit && (<button onClick={() => handleVendorEdit(vendor)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Edit"><KTIcon iconName='pencil' className='fs-3' /></button>)}
                          {(vendor_add || vendor_edit) && (<button onClick={() => handleStatusUpdate.mutate(vendor.id)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Toggle Status"><KTIcon iconName='switch' className='fs-3' /></button>)}
                          {vendor_logs && (<button onClick={() => handleVendorLogs(vendor)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Logs"><KTIcon iconName='notepad-bookmark' className='fs-3' /></button>)}
                          {vendor_delete && (<button onClick={() => setSelectedVendor(vendor)} className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' title="Delete" data-bs-toggle='modal' data-bs-target='#kt_modal_delete_vendor'><KTIcon iconName='trash' className='fs-3' /></button>)}
                        </div>
                      </td>
                      }
                    </tr>
                  ))
                ) : (
                  !isFetching && <tr><td colSpan={7} className="text-center py-5">No vendors found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer: Pagination and Items Per Page */}
        {totalItems > 0 && (
          <div className='card-footer d-flex justify-content-between align-items-center py-4'>
            <div>
              <span className='text-muted me-2'>Showing:</span>
              <select
                className='form-select form-select-sm d-inline-block w-auto me-3'
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                disabled={isFetching}
              >
                {itemsPerPageOptions.map(option => (<option key={option} value={option}>{option}</option>))}
              </select>
              <span className='text-muted'>
                {currentRangeFrom} - {currentRangeTo > totalItems ? totalItems : currentRangeTo} of {totalItems} vendors
              </span>
            </div>
          
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 || isFetching ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handlePreviousPage} disabled={currentPage === 1 || isFetching}>Previous</button>
                </li>
                {/* Optional: Page Number Logic */}
                <li className={`page-item ${currentPage === totalPages || totalPages === 0 || isFetching ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0 || isFetching}>Next</button>
                </li>
              </ul>
            </nav>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <div className='modal fade' tabIndex={-1} id='kt_modal_delete_vendor'>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Delete Vendor Confirmation</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className='modal-body'>
                <p>Are you sure you want to delete the vendor: <br/>
                  <strong>{selectedVendor?.supplier_name}</strong>?
                </p>
                <p className='text-muted small'>This action cannot be undone.</p>
              </div>
              <div className='modal-footer'>
                <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => { if (selectedVendor) handleDeleteVendor.mutate(selectedVendor.id); }}
                  data-bs-dismiss="modal"
                  disabled={handleDeleteVendor.isLoading}
                >
                  {handleDeleteVendor.isLoading ? 'Deleting...' : 'Delete Vendor'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default VendorsList;